var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useState, useEffect, useMemo, useCallback, memo } from 'react';
import { reportEndpointEvent } from '@/utils/endpoint-events';
import Editor, { loader } from '@monaco-editor/react';
// Self-host Monaco: point the loader at our own origin instead of the public
// jsDelivr CDN that `@monaco-editor/loader` uses by default. The `vs` runtime
// is copied to <publicPath>/monaco/vs at build time (copy-webpack-plugin in
// config/webpack.{dev,prod}.ts). This is why the editor kept failing to load on
// candidate networks behind corporate firewalls / VPNs that block third-party
// CDNs — now the assets are same-origin (already reachable; the interview WS is
// connected from the same host). Workers resolve relative to `vs`, so no
// MonacoEnvironment shim is needed while assets stay same-origin.
// Flag-off (REACT_APP_MONACO_SELF_HOST_ENABLED=false) restores CDN loading;
// REACT_APP_MONACO_VS_PATH overrides the path (default '/monaco/vs').
const MONACO_SELF_HOST_ENABLED = ((_a = process.env.REACT_APP_MONACO_SELF_HOST_ENABLED) !== null && _a !== void 0 ? _a : 'true').toLowerCase() !== 'false';
// Monaco's language workers (tsWorker, jsonWorker, …) boot from a blob/data URL
// and load their scripts from `vs` with fetch/importScripts. A root-relative
// path has no base URL inside that worker, so TypeScript/JavaScript threw
// "Failed to parse URL from /monaco/vs/language/typescript/tsWorker.js" the
// moment the editor opened. Resolving against the document once, here, gives
// the workers an absolute URL on the same origin the relative path pointed at.
const toAbsoluteUrl = (path) => {
    try {
        return typeof document !== 'undefined' ? new URL(path, document.baseURI).href : path;
    }
    catch (_a) {
        return path;
    }
};
const MONACO_VS_PATH = toAbsoluteUrl(process.env.REACT_APP_MONACO_VS_PATH || '/monaco/vs').replace(/\/$/, '');
// The editor surface matches the editor card (#0b1020) instead of vs-dark's
// grey #1e1e1e, so the toolbar and the code area read as one panel.
const EDITOR_THEME = 'vriti-dark';
const EDITOR_SURFACE = '#0b1020';
const defineEditorTheme = (monaco) => {
    monaco.editor.defineTheme(EDITOR_THEME, {
        base: 'vs-dark',
        inherit: true,
        rules: [],
        colors: {
            'editor.background': EDITOR_SURFACE,
            'editorGutter.background': EDITOR_SURFACE,
            'editor.lineHighlightBackground': '#ffffff0a',
            'editor.lineHighlightBorder': '#00000000',
            'editorLineNumber.foreground': '#475569',
            'editorLineNumber.activeForeground': '#cbd5e1',
            'editorIndentGuide.background1': '#ffffff12',
            'scrollbarSlider.background': '#ffffff1f',
            'scrollbarSlider.hoverBackground': '#ffffff33',
        },
    });
};
// Shown when the Monaco runtime cannot be loaded (blocked CDN, missing
// self-host assets, SPA fallback serving index.html for /monaco/vs/*, etc.).
const MONACO_LOAD_ERROR_MSG = 'Code editor could not be loaded. This is often caused by company firewalls, VPNs, or blocked scripts.';
// Shown in place of the countdown when the backend sent no time cap for the
// question (VA-27640) — there is no deadline, so there is no time to display.
const NO_TIME_CAP_LABEL = '--:--';
if (MONACO_SELF_HOST_ENABLED) {
    // Idempotent at module scope — runs once when this module is first imported,
    // before any <Editor> mounts.
    loader.config({ paths: { vs: MONACO_VS_PATH } });
    // eslint-disable-next-line no-console
    console.info('[coding-editor] Monaco self-host enabled vs=%s', MONACO_VS_PATH);
}
// Pre-fetch the Monaco runtime in the background BEFORE the candidate reaches a
// coding question (call this once when the interview screen mounts). loader
// caches globally, so when <Editor> later mounts it uses the already-fetched
// runtime and appears near-instantly instead of downloading a few MB at the
// worst possible moment on a slow network. Errors are ignored here — the real
// load/error handling still runs when the editor actually mounts. Importing
// this module has already applied the self-host loader.config above.
export function prewarmMonaco() {
    loader.init().catch(() => {
        /* ignore: surfaced at editor mount time if it truly fails */
    });
}
import { Check, ChevronDown, ChevronRight, Clock, Lightbulb, Maximize2, Minimize2, Mic, MicOff, Play, RotateCcw, Send, Sparkles, } from 'lucide-react';
import { useInterviewMeeting } from '@/context/realtime/realtime-interview-context';
import InterviewHeader from './interview-header';
import { splitIntoBubbles } from '@/utils/chatBubbles';
import { createSubmissionClaim } from '@/utils/submission-claim';
import { parseSubmittedCode } from '@/utils/submitted-code';
import { codingTotalSec } from '@/utils/coding-time';
import { CodeSnippet } from './code-snippet';
import { Skeleton } from './skeleton';
import QuestionProgressPill from './question-progress-pill';
import { isPasteFromEditor } from './editor-clipboard';
// Code execution ("Run") is temporarily disabled: the backend /api/run-code/
// sandbox isn't ready yet. Flip to `true` (or wire to an env flag) once the API
// is live to restore the Run button + console output. Submission is unaffected.
const CODE_EXECUTION_ENABLED = false;
// Languages the candidate can pick. `id` is the Monaco language id (drives
// syntax highlighting + indentation) and is also what we send to the backend
// /api/run-code/ runner; the BE maps it to a runtime.
const LANGUAGES = [
    { id: 'javascript', label: 'JavaScript', ext: 'js' },
    { id: 'typescript', label: 'TypeScript', ext: 'ts' },
    { id: 'python', label: 'Python', ext: 'py' },
    { id: 'java', label: 'Java', ext: 'java' },
    { id: 'cpp', label: 'C++', ext: 'cpp' },
    { id: 'c', label: 'C', ext: 'c' },
    { id: 'csharp', label: 'C#', ext: 'cs' },
    { id: 'go', label: 'Go', ext: 'go' },
    { id: 'ruby', label: 'Ruby', ext: 'rb' },
    { id: 'php', label: 'PHP', ext: 'php' },
    { id: 'sql', label: 'SQL', ext: 'sql' },
];
// Map a backend-supplied language string onto a known Monaco id.
const normalizeLanguage = (raw) => {
    const v = (raw || '').toLowerCase().trim();
    if (!v)
        return 'javascript';
    const alias = {
        js: 'javascript',
        node: 'javascript',
        nodejs: 'javascript',
        ts: 'typescript',
        py: 'python',
        python3: 'python',
        'c++': 'cpp',
        cplusplus: 'cpp',
        'c#': 'csharp',
        cs: 'csharp',
        golang: 'go',
        sqlite: 'sql',
        sqlite3: 'sql',
        mysql: 'sql',
        postgresql: 'sql',
        postgres: 'sql',
        psql: 'sql',
    };
    const mapped = alias[v] || v;
    return LANGUAGES.some((l) => l.id === mapped) ? mapped : 'javascript';
};
const labelForLang = (id) => { var _a; return ((_a = LANGUAGES.find((l) => l.id === id)) === null || _a === void 0 ? void 0 : _a.label) || id; };
const extForLang = (id) => { var _a; return ((_a = LANGUAGES.find((l) => l.id === id)) === null || _a === void 0 ? void 0 : _a.ext) || 'txt'; };
// Indent (in 2-space steps) and length of each placeholder code line — shaped
// like a function body so it reads as code, not as a paragraph.
const EDITOR_SKELETON_LINES = [
    [0, 46],
    [1, 62],
    [1, 38],
    [2, 54],
    [2, 30],
    [1, 12],
    [0, 0],
    [1, 58],
    [1, 24],
    [0, 8],
];
const EditorSkeleton = () => (_jsxs("div", { role: 'status', "aria-busy": 'true', className: 'flex h-full bg-[#0b1020] pt-3', children: [_jsx("span", { className: 'sr-only', children: "Loading editor" }), _jsxs("div", { "aria-hidden": 'true', className: 'flex w-full', children: [_jsx("div", { className: 'flex w-12 shrink-0 flex-col items-end gap-[9px] pr-4', children: EDITOR_SKELETON_LINES.map((_, i) => (_jsx(Skeleton, { inverse: true, className: 'h-2.5 w-3 rounded-sm' }, i))) }), _jsx("div", { className: 'flex flex-1 flex-col gap-[9px] pl-2', children: EDITOR_SKELETON_LINES.map(([indent, len], i) => len ? (_jsx(Skeleton, { inverse: true, className: 'h-2.5 rounded-sm', style: { marginLeft: indent * 24, width: `${len}%` } }, i)) : (_jsx("div", { className: 'h-2.5' }, i))) })] })] }));
const UserFallbackRenderer = memo(({ name }) => (_jsx("div", { className: 'flex h-full w-full flex-col items-center justify-center bg-slate-800', children: _jsx("div", { className: 'mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/80 text-sm font-semibold text-white', children: (name || 'You').charAt(0).toUpperCase() }) })));
UserFallbackRenderer.displayName = 'UserFallbackRenderer';
const CodingEditor = ({ isOpen, onClose, onSubmit, question }) => {
    var _a, _b, _c;
    const { wsRef, setCodeDefaultData, codeExplain, name, email, userStream, camEnabled, micEnabled, toggleMic, chat, submitCodingAnswer, skipCodingQuestion, confirmSkipCodingQuestion, runCode, registerActiveCodingEditorSnapshot = () => { }, timerPaused = false, reconnectPhase, dismissReconnectBanner, sttDeaf = false, sttProcessing = false, questionProgress = null, } = useInterviewMeeting();
    // VA-27633: the editor is a full-screen overlay with its OWN header, so
    // without this the candidate loses the Help button for the whole coding
    // question — the surface where things are most likely to go wrong, and
    // where a wedged interview is hardest to distinguish from thinking time.
    // Local ref for the small video tile inside the coding editor (the shared
    // context videoRef is bound to the meeting screen's main tile).
    const editorVideoRef = useRef(null);
    // NOTE: proctor_layout reporting for the coding-editor PiP is handled centrally
    // by useProctorCameraLayout (mounted in MeetingScreen), which reads the DOM and
    // emits for whichever camera tile is actually on screen. The PiP <video> and
    // the editor root below are tagged (data-proctor-cam / data-proctor-editor) so
    // that coordinator can find them — no per-editor emit effect needed here.
    const ques = question;
    // Stable identity for the active coding question (unchanged across the
    // problem_statement → solution_explanation → complexity_discussion phases).
    const questionKey = question.editor_instance_key ||
        `${(_a = question.question_id) !== null && _a !== void 0 ? _a : 'dynamic'}-${(_b = question.question_index) !== null && _b !== void 0 ? _b : 0}`;
    const [code, setCode] = useState('');
    const [language, setLanguage] = useState(() => normalizeLanguage(question.programming_language));
    const [langOpen, setLangOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [hints, setHints] = useState(false);
    const [showSkipConfirm, setShowSkipConfirm] = useState(false);
    // Console (Run output) state — independent of the submission flow.
    const [running, setRunning] = useState(false);
    const [runResult, setRunResult] = useState(null);
    // ✅ DRIFT-FREE TIMER
    const [timeLeft, setTimeLeft] = useState(0);
    const startRef = useRef(null);
    const timeLeftRef = useRef(0);
    const [isMobile, setIsMobile] = useState(false);
    // Bind the camera to the editor's small video tile.
    useEffect(() => {
        const target = editorVideoRef.current;
        if (!target)
            return;
        if (!isOpen) {
            target.srcObject = null;
            return;
        }
        const visibleTrack = userStream && camEnabled ? userStream.getVideoTracks()[0] : null;
        if (!visibleTrack) {
            target.srcObject = null;
            return;
        }
        const previewTrack = visibleTrack.clone();
        const previewStream = new MediaStream([previewTrack]);
        target.srcObject = previewStream;
        target.play().catch(() => {
            /* ignore autoplay timing issues */
        });
        return () => {
            target.srcObject = null;
            previewTrack.stop();
        };
        // `expanded` (and isMobile) unmount the camera tile; when it comes back it
        // is a NEW <video> element, which stayed black until the stream was rebound.
    }, [userStream, camEnabled, isOpen, expanded, isMobile]);
    const [monacoError, setMonacoError] = useState(null);
    // Bumping this remounts <Editor> and restarts the load watchdog — used by the
    // "Reload editor" retry button so a transient load failure is recoverable
    // without restarting the whole interview.
    const [monacoReloadNonce, setMonacoReloadNonce] = useState(0);
    const monacoLoadTimeoutRef = useRef(null);
    const editorRef = useRef(null);
    const consoleRef = useRef(null);
    const transcriptRef = useRef(null);
    // VA-27640: `null` means the backend sent no cap for this question. That is NOT
    // the same as a cap of zero — a zero total puts the clock at 00:00 the moment the
    // editor opens, which trips the auto-submit-on-expiry effect below and submits an
    // empty answer before the candidate can type a character. When there is no cap we
    // simply run no countdown: the clock reads `--:--` and nothing auto-submits.
    const cappedTotalSec = codingTotalSec(ques);
    const hasTimeCap = cappedTotalSec !== null;
    const totalSec = cappedTotalSec !== null && cappedTotalSec !== void 0 ? cappedTotalSec : 0;
    // Detect device width (mobile stacks the columns).
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 900);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    // Reset language when a genuinely new question opens.
    useEffect(() => {
        setLanguage(normalizeLanguage(question.programming_language));
        setRunResult(null);
        setRunning(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionKey]);
    // ✅ RESET TIMER ON OPEN + QUESTION CHANGE
    useEffect(() => {
        if (!isOpen)
            return;
        // CL-8: honor a persisted remaining time (folded in by the periodic persist
        // on reconnect/refresh) so the countdown resumes where it left off instead of
        // resetting to the full estimate. Anchor startRef so the drift loop keeps
        // computing the correct remaining. Absent/invalid → full estimate (fresh Q).
        const persistedRemaining = typeof ques.remainingTimeSec === 'number' && ques.remainingTimeSec >= 0
            ? Math.min(totalSec, ques.remainingTimeSec)
            : totalSec;
        startRef.current = Date.now() - (totalSec - persistedRemaining) * 1000;
        setTimeLeft(persistedRemaining);
        autoSubmittedRef.current = false;
        // TS-11: a NEW question (or a reopen of this one) gets a fresh submission
        // claim — otherwise the one-shot guard would block its first real submit.
        submissionClaimRef.current.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, questionKey]);
    const autoSubmittedRef = useRef(false);
    // TS-11: one-shot re-entrancy claim for `submit()` — see submission-claim.ts
    // for why this is separate from `isSubmittingRef`/`hasSubmittedRef`.
    const submissionClaimRef = useRef(createSubmissionClaim());
    // CL-20: track mount so a delayed post-submit state reset can't touch an
    // unmounted editor (the editor usually unmounts synchronously on submit).
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    const getCurrentCode = useCallback(() => {
        var _a;
        const editorValue = typeof ((_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.getValue) === 'function' ? editorRef.current.getValue() : code;
        return editorValue || '';
    }, [code]);
    // Submission state (spinner / "Submitted" label). Reset on close / new question.
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    useEffect(() => {
        if (!isOpen) {
            setIsSubmitting(false);
            setHasSubmitted(false);
        }
    }, [isOpen]);
    useEffect(() => {
        setIsSubmitting(false);
        setHasSubmitted(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionKey]);
    // Pause/resume the per-question timer with the connection state (wall-clock).
    const pausedAtRef = useRef(null);
    useEffect(() => {
        if (!isOpen)
            return;
        if (timerPaused) {
            if (pausedAtRef.current == null)
                pausedAtRef.current = Date.now();
        }
        else if (pausedAtRef.current != null && startRef.current != null) {
            startRef.current += Date.now() - pausedAtRef.current;
            pausedAtRef.current = null;
        }
    }, [timerPaused, isOpen]);
    // ✅ DRIFT-FREE TIMER LOOP
    useEffect(() => {
        if (!isOpen)
            return;
        // No cap → nothing to count down. `startRef` still anchors elapsed time so the
        // time-spent bookkeeping below stays accurate.
        if (!hasTimeCap)
            return;
        const interval = setInterval(() => {
            if (!startRef.current || pausedAtRef.current != null)
                return;
            const elapsed = Math.floor((Date.now() - startRef.current) / 1000);
            const remaining = totalSec - elapsed;
            setTimeLeft(remaining > 0 ? remaining : 0);
        }, 300);
        return () => clearInterval(interval);
    }, [isOpen, totalSec, hasTimeCap]);
    useEffect(() => {
        timeLeftRef.current = timeLeft;
    }, [timeLeft]);
    // Seconds the candidate has spent on this question, for the skip/submit payloads.
    // With a cap the countdown is authoritative (it already excludes paused time).
    // VA-27640: without a cap there is no countdown, so `totalSec - timeLeft` would
    // report a flat 0 no matter how long they worked — measure elapsed wall time from
    // `startRef` instead, which the pause handler keeps adjusted.
    const timeSpentSoFar = useCallback(() => {
        if (hasTimeCap)
            return Math.max(0, totalSec - Math.max(0, timeLeftRef.current));
        if (startRef.current == null)
            return 0;
        if (pausedAtRef.current != null) {
            return Math.max(0, Math.floor((pausedAtRef.current - startRef.current) / 1000));
        }
        return Math.max(0, Math.floor((Date.now() - startRef.current) / 1000));
    }, [hasTimeCap, totalSec]);
    // Seed the editor with the question's starter code on question change.
    useEffect(() => {
        var _a;
        const initial = question.initialCode || '';
        setCode(initial);
        if (typeof ((_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.setValue) === 'function') {
            editorRef.current.setValue(initial);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionKey]);
    const fmt = useCallback((s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    }, []);
    // Timer colour by fraction remaining. An uncapped question never turns amber/red —
    // there is no deadline to warn about.
    const timerColor = useMemo(() => {
        if (!hasTimeCap)
            return 'text-slate-300';
        const frac = totalSec > 0 ? timeLeft / totalSec : 1;
        if (frac >= 0.5)
            return 'text-emerald-300';
        if (frac >= 0.25)
            return 'text-amber-300';
        return 'text-red-300';
    }, [timeLeft, totalSec, hasTimeCap]);
    useEffect(() => {
        if (isOpen)
            document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);
    // Monaco load watchdog (firewall / blocked-script detection). Now that the
    // runtime is self-hosted (same-origin) the default timeout is more generous —
    // a same-origin asset fetch on a slow link can still take a few seconds, and
    // a premature error here is worse than waiting. Configurable via
    // REACT_APP_MONACO_LOAD_TIMEOUT_MS. Re-runs on monacoReloadNonce (retry).
    useEffect(() => {
        if (!isOpen)
            return;
        // Generous backstop: this timer ONLY guards a true silent stall (a network
        // that neither delivers the runtime nor errors). A slow-but-successful load
        // clears it via loader.init().then below, and a hard failure surfaces
        // instantly via .catch — so this must NOT be so short that a slow (but
        // working) connection false-fails. Overridable via env.
        const timeoutMs = Number(process.env.REACT_APP_MONACO_LOAD_TIMEOUT_MS) || 30000;
        setMonacoError(null);
        let cancelled = false;
        monacoLoadTimeoutRef.current = setTimeout(() => {
            if (cancelled)
                return;
            // eslint-disable-next-line no-console
            console.error('[coding-editor] Monaco failed to load within %dms', timeoutMs);
            setMonacoError(MONACO_LOAD_ERROR_MSG);
        }, timeoutMs);
        // loader.init() resolves once the vs runtime is fetched + evaluated (even
        // slowly) and rejects on a hard failure (blocked CDN, 404, or index.html
        // served for /monaco/vs/* by an SPA catch-all → AMD parse error).
        loader
            .init()
            .then(() => {
            // Success at ANY speed → cancel the watchdog so a slow-but-successful
            // load never false-fails. onMount still fires to wire the editor.
            if (cancelled)
                return;
            if (monacoLoadTimeoutRef.current)
                clearTimeout(monacoLoadTimeoutRef.current);
        })
            .catch((err) => {
            // Hard failure → show the fallback immediately instead of waiting out
            // the backstop timeout.
            if (cancelled)
                return;
            if (monacoLoadTimeoutRef.current)
                clearTimeout(monacoLoadTimeoutRef.current);
            // eslint-disable-next-line no-console
            console.error('[coding-editor] Monaco loader.init() failed', err);
            setMonacoError(MONACO_LOAD_ERROR_MSG);
        });
        return () => {
            cancelled = true;
            if (monacoLoadTimeoutRef.current)
                clearTimeout(monacoLoadTimeoutRef.current);
        };
    }, [isOpen, monacoReloadNonce]);
    // Keep the transcript pinned to the newest message.
    // Runs on open too: the panel mounts with the editor, and without `isOpen`
    // it opened scrolled to the top of a long conversation. The frame wait lets
    // code blocks and wrapped bubbles finish layout before measuring.
    useEffect(() => {
        if (!isOpen)
            return;
        const id = requestAnimationFrame(() => {
            const el = transcriptRef.current;
            if (el)
                el.scrollTop = el.scrollHeight;
        });
        return () => cancelAnimationFrame(id);
    }, [chat, expanded, isOpen, isMobile]);
    // Auto-scroll the console when new output lands.
    useEffect(() => {
        if (consoleRef.current)
            consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }, [runResult, running]);
    useEffect(() => {
        if (!isOpen)
            return;
        const key = (e) => {
            // TS-11: Ctrl/Cmd+Enter is bound in the Monaco editor itself (see the
            // `addCommand` in onMount). Binding it HERE as well meant one keypress ran
            // `submit()` twice — Monaco handles the key internally and the event still
            // bubbles to `document`, so `preventDefault()` does not stop the other
            // handler. The re-entrancy claim in `submit()` now makes that harmless, but
            // one shortcut should have exactly one owner. Monaco keeps it (it is the
            // focused surface); this listener keeps only Escape.
            if (e.key === 'Escape')
                setShowSkipConfirm((s) => !s);
        };
        document.addEventListener('keydown', key);
        return () => document.removeEventListener('keydown', key);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, code, timeLeft]);
    // Skip / close — tells the orchestrator the candidate left this question
    // Skip is now confirmed INSIDE the editor (the editor stays open and shows a
    // confirmation modal) instead of bouncing to a choice popup on the main
    // screen. `requestSkip` opens the modal; `commitSkip` performs the real skip.
    const requestSkip = useCallback(() => setShowSkipConfirm(true), []);
    const commitSkip = useCallback(() => {
        const timeSpent = timeSpentSoFar();
        // skipCodingQuestion freezes the pending snapshot + closes the editor;
        // confirmSkipCodingQuestion commits it (notifies the AI to move on, saves a
        // reopen-able entry so the candidate can still resume from the chat).
        skipCodingQuestion(timeSpent);
        confirmSkipCodingQuestion();
        setShowSkipConfirm(false);
        setHints(false);
        setCodeDefaultData();
        onClose();
    }, [onClose, timeSpentSoFar, skipCodingQuestion, confirmSkipCodingQuestion, setCodeDefaultData]);
    // Refs mirror the latest submission flags for the keydown closure.
    const isSubmittingRef = useRef(false);
    const hasSubmittedRef = useRef(false);
    useEffect(() => {
        isSubmittingRef.current = isSubmitting;
    }, [isSubmitting]);
    useEffect(() => {
        hasSubmittedRef.current = hasSubmitted;
    }, [hasSubmitted]);
    const getRealtimeCodingSnapshot = useCallback(() => {
        return {
            code: getCurrentCode(),
            timeSpentSec: timeSpentSoFar(),
            hasSubmitted: isSubmittingRef.current || hasSubmittedRef.current,
        };
    }, [getCurrentCode, timeSpentSoFar]);
    useEffect(() => {
        if (!isOpen) {
            registerActiveCodingEditorSnapshot(null);
            return;
        }
        registerActiveCodingEditorSnapshot(getRealtimeCodingSnapshot);
        return () => registerActiveCodingEditorSnapshot(null);
    }, [getRealtimeCodingSnapshot, isOpen, registerActiveCodingEditorSnapshot]);
    const submit = useCallback(() => {
        var _a, _b, _c, _d;
        // TS-11: one submission per question, claimed synchronously before ANY
        // branching. The empty-editor branch below used to `return` without setting
        // `isSubmittingRef`/`hasSubmittedRef`, leaving `submit()` freely re-entrant
        // from all five call sites (button, document keydown, Monaco Ctrl+Enter,
        // timer auto-submit, idle Swal) — and the Submit button stayed enabled too,
        // since `actionsDisabled` keys off those same flags. Ctrl+Enter alone fires
        // it twice (the document listener AND the Monaco command see one keypress),
        // so an empty submit sent `code_submission_complete` TWICE. The backend's
        // dedupe guard then disabled the mic and cancelled the watchdog before
        // returning, wedging the interview on "listening". Both ends are now fixed;
        // this is the trigger side.
        //
        // This is a DEDICATED claim, not the existing two flags: those are also read
        // by `getRealtimeCodingSnapshot` to tell the context whether the question was
        // already submitted, and flipping them early would make `skipCodingQuestion`
        // take its short-circuit path and never send the skip at all.
        if (isSubmittingRef.current || hasSubmittedRef.current)
            return;
        if (!submissionClaimRef.current.tryClaim())
            return;
        const timeSpent = timeSpentSoFar();
        const currentCode = getCurrentCode();
        // R7: pressing Submit with an EMPTY editor is a skip, not a submission.
        // Route it through the SAME skip state path as the "Back to interview"
        // button (skipCodingQuestion → coding_editor_paused, confirmSkipCodingQuestion
        // → submission_action:'skip') so the backend clears its authoritative
        // coding-editor-open flag and marks the question SKIPPED. A plain empty
        // submission does NOT clear that flag, so the assistant keeps re-instructing
        // "press the Back to interview button" against an already-closed editor and
        // the interview gets stuck on this question in a loop. Both modes share these
        // context functions, so this fixes realtime and cost-optimized alike.
        if (!currentCode.trim()) {
            console.info('[coding-editor] empty submit → routing through skip flow', {
                language,
                questionId: (_a = ques.question_id) !== null && _a !== void 0 ? _a : null,
                questionIndex: (_b = ques.question_index) !== null && _b !== void 0 ? _b : null,
            });
            skipCodingQuestion(timeSpent);
            confirmSkipCodingQuestion();
            setShowSkipConfirm(false);
            setHints(false);
            setCodeDefaultData();
            onClose();
            return;
        }
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        setCode(currentCode);
        console.info('[coding-editor] submitting monaco value', {
            length: currentCode.length,
            language,
            questionId: (_c = ques.question_id) !== null && _c !== void 0 ? _c : null,
            questionIndex: (_d = ques.question_index) !== null && _d !== void 0 ? _d : null,
        });
        submitCodingAnswer(currentCode, timeSpent, language);
        // CL-20: the editor usually unmounts synchronously on submit (the context
        // closes it), so mark "submitted" immediately and guard the delayed reset
        // against running on an unmounted editor.
        hasSubmittedRef.current = true;
        setTimeout(() => {
            if (!mountedRef.current)
                return;
            isSubmittingRef.current = false;
            setIsSubmitting(false);
            setHasSubmitted(true);
        }, 800);
        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(currentCode, timeSpent);
    }, [
        getCurrentCode,
        timeSpentSoFar,
        language,
        onSubmit,
        ques.question_id,
        ques.question_index,
        submitCodingAnswer,
        skipCodingQuestion,
        confirmSkipCodingQuestion,
        setCodeDefaultData,
        onClose,
    ]);
    // Auto-submit when the per-question timer hits zero.
    useEffect(() => {
        if (!isOpen)
            return;
        // VA-27640: an uncapped question has no expiry to auto-submit on. Without this
        // guard `totalSec` is 0, `timeLeft` starts at 0, and the elapsed check below
        // (`0 < -1`) passes — auto-submitting an empty answer on the first render.
        if (!hasTimeCap)
            return;
        if (autoSubmittedRef.current)
            return;
        if (timeLeft > 0)
            return;
        if (!startRef.current)
            return;
        const elapsedSec = (Date.now() - startRef.current) / 1000;
        if (elapsedSec < totalSec - 1)
            return;
        autoSubmittedRef.current = true;
        console.info('[coding-editor] timer expired, auto-submitting current code', {
            length: code.length,
        });
        submit();
    }, [isOpen, timeLeft]); // eslint-disable-line react-hooks/exhaustive-deps
    // Run the code against the backend sandbox (does NOT submit).
    const handleRun = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (running)
            return;
        setRunning(true);
        setRunResult(null);
        const res = yield runCode(getCurrentCode(), language);
        setRunResult(res);
        setRunning(false);
    }), [running, runCode, getCurrentCode, language]);
    const handleReset = useCallback(() => {
        var _a;
        const initial = ques.initialCode || '';
        setCode(initial);
        if (typeof ((_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.setValue) === 'function')
            editorRef.current.setValue(initial);
        setRunResult(null);
    }, [ques.initialCode]);
    // CL-22: NO idle nudge during a coding question. While the editor is open the
    // candidate must be able to work undisturbed — the assistant must NEVER speak on
    // its own (the old 60s idle prompt fired `response.create` / an acknowledgment,
    // interrupting focus). The assistant now responds ONLY when the candidate
    // actually says something (routed through the normal transcript flow), staying
    // on the same coding question. We keep only the passive idle bookkeeping and the
    // VA-27761 follow-up: the idle auto-submit is GONE.
    //
    // It submitted the candidate's code after 120s without a mousemove, keypress,
    // click or scroll — behind a modal that blocked the editor (no outside click, no
    // escape) and submitted anyway when its own 8s timer ran out.
    //
    // Sitting still is not idleness during a coding task. Reading the problem,
    // thinking about an approach, or working it out on paper all produce no input
    // events, and this fired on a four-minute question with 3:56 still on the clock
    // (interview-12ad99af) — four seconds from submitting unfinished work.
    //
    // Nor did it protect anything. The per-question timer is the real bound and
    // already auto-submits on expiry, which is what "the per-question timer is the
    // real time bound" in the old comment conceded. The 60s half of it called
    // `setCodingPaused`, which is a no-op in BOTH modes, so that half did nothing at
    // all.
    const problemRef = useRef(null);
    useEffect(() => {
        const el = problemRef.current;
        if (!el)
            return;
        const prevent = (e) => e.preventDefault();
        el.addEventListener('selectstart', prevent);
        return () => el.removeEventListener('selectstart', prevent);
    }, []);
    // Real conversation for the live transcript panel (skip system notices).
    const transcript = useMemo(() => chat.filter((m) => (m === null || m === void 0 ? void 0 : m.text) && !(m === null || m === void 0 ? void 0 : m.status)), [chat]);
    const actionsDisabled = codeExplain || isSubmitting || hasSubmitted;
    // CL-14: "Back to interview" must stay usable during the post-submission
    // walkthrough (codeExplain) — otherwise every button is disabled and, with the
    // button-only-close guard, the candidate is trapped in the editor. During the
    // walkthrough Back just closes the editor (the code was already submitted, so
    // it is NOT a skip); before submission it opens the skip-confirm as before.
    const backDisabled = isSubmitting || hasSubmitted;
    if (!isOpen)
        return null;
    // Layout by width (isMobile is < 768px, same as aiMd):
    //  · mobile          → stacked: problem above the editor
    //  · 768–1023px      → two columns: problem over camera/transcript | editor
    //  · 1024–1279px     → three columns with slimmer side panels
    //  · ≥ 1280px        → three columns at full width
    // Full-width mode shows the editor alone. Placement is pure CSS so the
    // camera <video> keeps the same element across breakpoints.
    const threeUp = !isMobile && !expanded;
    const bodyLayout = isMobile
        ? 'flex flex-col'
        : expanded
            ? 'grid grid-cols-1'
            : 'grid grid-cols-[260px_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_minmax(0,1fr)] aiLg:grid-cols-[260px_minmax(0,1fr)_280px] aiLg:grid-rows-1 aiXl:grid-cols-[300px_minmax(0,1fr)_340px]';
    return (_jsxs("div", { "data-proctor-editor": true, className: 'fixed inset-0 z-50 flex flex-col bg-muted text-foreground', children: [_jsx(InterviewHeader, { name: name, email: email }), _jsxs("div", { className: `min-h-0 flex-1 gap-4 overflow-hidden p-3 aiSm:p-4 ${bodyLayout}`, children: [!expanded && (_jsxs("aside", { className: `flex min-h-0 shrink-0 flex-col overflow-y-auto rounded-2xl border border-border bg-card p-4 shadow-[var(--shadow-soft)] aiXl:p-5 ${isMobile ? 'max-h-[38%] w-full' : 'col-start-1 row-start-1'}`, children: [_jsxs("button", { onClick: codeExplain ? onClose : requestSkip, disabled: backDisabled, "aria-label": 'Back to interview', className: 'mb-4 inline-flex items-center gap-1.5 self-start rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-semibold text-foreground shadow-[var(--shadow-soft)] transition hover:border-primary hover:bg-muted hover:text-primary disabled:cursor-not-allowed disabled:opacity-50', children: [_jsx("span", { "aria-hidden": 'true', className: 'text-base leading-none', children: "\u2190" }), "Back to interview"] }), _jsxs("div", { ref: problemRef, role: 'region', "aria-label": 'Problem statement', onCopy: (e) => e.preventDefault(), onCut: (e) => e.preventDefault(), onContextMenu: (e) => e.preventDefault(), children: [_jsxs("p", { className: 'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground', children: [ques.question_index ? `Problem ${ques.question_index}` : 'Problem', ques.difficulty ? ` · ${ques.difficulty}` : ''] }), _jsx("h2", { className: 'mt-1 break-words text-lg font-bold leading-snug text-foreground aiXl:text-xl', children: ques.question_title }), _jsx("div", { className: 'mt-3 whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/80', children: ques.question_description }), Array.isArray(ques.sample_data) && ques.sample_data.length > 0 && (_jsx("div", { className: 'mt-4 space-y-2', children: ques.sample_data.map((s, i) => (_jsxs("div", { className: 'rounded-xl bg-muted px-3 py-2 font-mono text-xs leading-relaxed text-foreground', children: [s.input && (_jsxs("div", { children: [_jsx("span", { className: 'text-muted-foreground', children: "Input:" }), " ", s.input] })), s.output && (_jsxs("div", { children: [_jsx("span", { className: 'text-muted-foreground', children: "Output:" }), " ", s.output] })), s.explanation && (_jsx("div", { className: 'mt-1 text-muted-foreground', children: s.explanation }))] }, i))) })), _jsxs("div", { className: 'mt-5 rounded-xl border border-border bg-muted/50 p-3', children: [_jsx("p", { className: 'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground', children: "Tips" }), _jsxs("ul", { className: 'mt-1.5 space-y-1.5 text-[13px] leading-relaxed text-foreground/80', children: [_jsx("li", { children: "Think out loud \u2014 the AI evaluates reasoning, not just the answer." }), CODE_EXECUTION_ENABLED && (_jsx("li", { children: "You can run code anytime to check intermediate output." })), _jsx("li", { children: "Discuss tradeoffs before optimizing." })] })] }), ques.hints && ques.hints.length > 0 && (_jsxs("div", { className: 'mt-4', children: [_jsxs("button", { onClick: () => setHints((h) => !h), className: 'flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:text-primary/80', children: [_jsx(Lightbulb, { className: 'h-4 w-4' }), hints ? 'Hide' : 'Show', " Hints", _jsx(ChevronRight, { className: `h-3.5 w-3.5 transition-transform ${hints ? 'rotate-90' : ''}` })] }), hints && (_jsx("div", { className: 'mt-2 space-y-2', children: ques.hints.map((h, i) => (_jsxs("div", { className: 'rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900', children: [_jsxs("span", { className: 'font-semibold', children: ["Hint ", i + 1, ":"] }), " ", h] }, i))) }))] }))] })] })), _jsxs("div", { className: `flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-[#0b1020] shadow-[var(--shadow-card)] ${threeUp ? 'col-start-2 row-span-2 row-start-1 aiLg:row-span-1' : ''}`, children: [_jsxs("div", { className: 'flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 py-2.5 aiSm:px-4', children: [_jsxs("div", { className: 'flex min-w-0 items-center gap-3', children: [_jsxs("span", { className: `truncate rounded-md bg-white/10 px-2.5 py-1 font-mono text-xs text-slate-200 ${expanded ? '' : 'hidden aiXl:inline'}`, children: ["solution.", extForLang(language)] }), _jsxs("span", { className: `flex items-center gap-1 font-mono text-sm font-semibold ${timerColor}`, children: [_jsx(Clock, { className: 'h-4 w-4' }), hasTimeCap ? fmt(timeLeft) : NO_TIME_CAP_LABEL] }), _jsx("span", { className: 'hidden aiSm:inline-flex', children: _jsx(QuestionProgressPill, { progress: questionProgress, tone: 'dark' }) })] }), _jsxs("div", { className: 'flex flex-wrap items-center gap-2', children: [_jsxs("div", { className: 'relative', children: [_jsxs("button", { type: 'button', onClick: () => setLangOpen((o) => !o), "aria-haspopup": 'listbox', "aria-expanded": langOpen, "aria-label": `Language: ${labelForLang(language)}`, className: 'inline-flex h-8 items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 text-xs font-medium text-slate-100 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60', children: [labelForLang(language), _jsx(ChevronDown, { className: 'h-3.5 w-3.5' })] }), langOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: 'fixed inset-0 z-10', onClick: () => setLangOpen(false) }), _jsx("div", { role: 'listbox', "aria-label": 'Language', className: 'absolute right-0 z-20 mt-1.5 max-h-[min(22rem,60vh)] w-44 overflow-y-auto rounded-xl border border-white/10 bg-[#11182b] p-1 shadow-xl', children: LANGUAGES.map((l) => (_jsxs("button", { type: 'button', role: 'option', "aria-selected": l.id === language, onClick: () => {
                                                                        setLanguage(l.id);
                                                                        setLangOpen(false);
                                                                    }, className: `flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-xs transition ${l.id === language
                                                                        ? 'bg-primary/25 font-semibold text-white'
                                                                        : 'text-slate-200 hover:bg-white/10'}`, children: [l.label, l.id === language && _jsx(Check, { className: 'h-3.5 w-3.5 text-[#8fa2ff]' })] }, l.id))) })] }))] }), _jsxs("button", { type: 'button', onClick: handleReset, disabled: actionsDisabled, title: 'Reset to starter code', "aria-label": 'Reset to starter code', className: 'inline-flex h-8 items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5 text-xs font-medium text-slate-100 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:opacity-50 aiXl:px-3', children: [_jsx(RotateCcw, { className: 'h-3.5 w-3.5' }), _jsx("span", { className: expanded ? '' : 'hidden aiXl:inline', children: "Reset" })] }), _jsx("button", { type: 'button', onClick: () => setExpanded((e) => !e), title: expanded ? 'Exit full width' : 'Full width editor', "aria-label": expanded ? 'Exit full width' : 'Full width editor', className: 'grid h-8 w-8 place-items-center rounded-lg border border-white/15 bg-white/5 text-slate-100 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60', children: expanded ? _jsx(Minimize2, { className: 'h-4 w-4' }) : _jsx(Maximize2, { className: 'h-4 w-4' }) }), CODE_EXECUTION_ENABLED && (_jsxs("button", { onClick: handleRun, disabled: running, className: 'inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-semibold text-primary-foreground shadow-sm transition hover:opacity-95 active:scale-95 disabled:opacity-60', style: { backgroundImage: 'var(--gradient-primary)' }, children: [running ? (_jsx("span", { className: 'h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent' })) : (_jsx(Play, { className: 'h-3.5 w-3.5' })), "Run"] })), _jsx("button", { type: 'button', onClick: submit, disabled: actionsDisabled, className: 'inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 active:scale-95 disabled:opacity-60', children: isSubmitting ? (_jsxs(_Fragment, { children: [_jsx("span", { className: 'h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent' }), "Submitting\u2026"] })) : hasSubmitted ? ('Submitted') : (_jsxs(_Fragment, { children: [_jsx(Send, { className: 'h-3.5 w-3.5' }), " Submit"] })) })] })] }), _jsx("div", { className: 'min-h-0 flex-1', children: monacoError ? (_jsxs("div", { className: 'flex h-full flex-col items-center justify-center px-6 text-center', children: [_jsx("p", { className: 'text-lg font-semibold text-red-400', children: "Editor failed to load" }), _jsx("p", { className: 'mt-2 max-w-md text-sm text-slate-400', children: monacoError }), _jsx("p", { className: 'mt-3 text-xs text-slate-500', children: "Try switching networks, disabling VPN, or using a non-restricted browser." }), _jsxs("button", { type: 'button', onClick: () => {
                                                // eslint-disable-next-line no-console
                                                console.info('[coding-editor] manual Monaco reload requested');
                                                setMonacoError(null);
                                                setMonacoReloadNonce((n) => n + 1);
                                            }, className: 'mt-4 inline-flex items-center gap-2 rounded-md bg-slate-700 px-4 py-2 text-sm font-medium text-slate-100 hover:bg-slate-600', children: [_jsx(RotateCcw, { className: 'h-4 w-4' }), " Reload editor"] })] })) : (_jsx(Editor, { height: '100%', 
                                    // Editor-shaped placeholder on the vs-dark surface (gutter +
                                    // indented code lines) so the pane keeps its layout while the
                                    // Monaco runtime loads.
                                    loading: _jsx(EditorSkeleton, {}), language: language, value: code, onChange: (v) => setCode(v || ''), onMount: (editor, monaco) => {
                                        if (monacoLoadTimeoutRef.current)
                                            clearTimeout(monacoLoadTimeoutRef.current);
                                        setMonacoError(null);
                                        editorRef.current = editor;
                                        editor.addCommand(monaco.KeyCode.Tab, () => editor.trigger('keyboard', 'tab', {}));
                                        editor.addCommand(monaco.KeyMod.Shift | monaco.KeyCode.Tab, () => editor.trigger('keyboard', 'outdentLines', {}));
                                        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => submit());
                                        const dom = editor.getDomNode();
                                        if (dom) {
                                            // VA-28131 step 2: a paste into the editor is the clearest
                                            // evidence there is that a solution came from somewhere
                                            // else, so it is blocked AND reported.
                                            // Integrity v2.1: copy and cut work normally, and so does
                                            // pasting the candidate's own code back (see
                                            // editor-clipboard). Only text from outside is blocked.
                                            // Capture phase: Monaco handles paste on its inner
                                            // textarea, before a bubbling listener here would run.
                                            let beforeLastCut = '';
                                            dom.addEventListener('cut', () => {
                                                beforeLastCut = editor.getValue();
                                            }, true);
                                            dom.addEventListener('contextmenu', (e) => e.preventDefault());
                                            dom.addEventListener('paste', (e) => {
                                                var _a, _b;
                                                // Read from the event rather than the clipboard API,
                                                // which needs a permission we do not have and would
                                                // prompt the candidate mid-interview.
                                                const pasted = (_b = (_a = e.clipboardData) === null || _a === void 0 ? void 0 : _a.getData('text')) !== null && _b !== void 0 ? _b : '';
                                                if (isPasteFromEditor(pasted, [editor.getValue(), beforeLastCut]))
                                                    return;
                                                e.preventDefault();
                                                e.stopPropagation();
                                                // Size separates a stray keystroke from a solution
                                                // arriving whole.
                                                reportEndpointEvent('paste_blocked', { size: pasted.length });
                                            }, true);
                                        }
                                    }, beforeMount: defineEditorTheme, theme: EDITOR_THEME, options: {
                                        minimap: { enabled: false },
                                        scrollBeyondLastLine: false,
                                        fontSize: isMobile ? 12 : 14,
                                        lineNumbers: 'on',
                                        automaticLayout: true,
                                        tabSize: 2,
                                        renderWhitespace: 'none',
                                        accessibilitySupport: 'on',
                                        wordWrap: 'on',
                                        cursorBlinking: 'smooth',
                                        quickSuggestions: true,
                                        suggestOnTriggerCharacters: true,
                                        acceptSuggestionOnEnter: 'on',
                                        tabCompletion: 'on',
                                        wordBasedSuggestions: 'matchingDocuments',
                                        parameterHints: { enabled: true },
                                        snippetSuggestions: 'inline',
                                        padding: { top: 12 },
                                    } }, `${questionKey}:${monacoReloadNonce}`)) }), CODE_EXECUTION_ENABLED && (_jsxs("div", { className: 'flex h-[28%] min-h-[120px] flex-col border-t border-white/10', children: [_jsxs("div", { className: 'flex items-center justify-between px-3 py-1.5 aiSm:px-4', children: [_jsx("span", { className: 'font-mono text-[11px] font-semibold uppercase tracking-wide text-slate-400', children: ">_ Console" }), _jsx("button", { onClick: () => setRunResult(null), className: 'text-[11px] text-slate-400 transition hover:text-slate-200', children: "Clear" })] }), _jsx("div", { ref: consoleRef, className: 'flex-1 overflow-y-auto px-3 pb-3 font-mono text-xs leading-relaxed aiSm:px-4', children: running ? (_jsx("span", { className: 'text-slate-400', children: "Running\u2026" })) : !runResult ? (_jsx("span", { className: 'text-emerald-400/70', children: "// Click Run to execute your code" })) : (_jsxs("div", { className: 'space-y-1 whitespace-pre-wrap break-words', children: [runResult.error && _jsx("div", { className: 'text-red-400', children: runResult.error }), runResult.stdout && _jsx("div", { className: 'text-slate-100', children: runResult.stdout }), runResult.stderr && _jsx("div", { className: 'text-red-400', children: runResult.stderr }), !runResult.error && (_jsxs("div", { className: 'pt-1 text-[11px] text-slate-500', children: ["exit code ", (_c = runResult.exit_code) !== null && _c !== void 0 ? _c : 0, typeof runResult.time_ms === 'number' ? ` · ${runResult.time_ms} ms` : ''] }))] })) })] }))] }), !expanded && !isMobile && (_jsxs("aside", { className: 'col-start-1 row-start-2 flex min-h-0 min-w-0 flex-col gap-4 aiLg:col-start-3 aiLg:row-start-1', children: [_jsxs("div", { className: 'relative aspect-video shrink-0 overflow-hidden rounded-2xl border border-border bg-slate-900 shadow-[var(--shadow-soft)]', children: [camEnabled && userStream ? (_jsx("video", { ref: editorVideoRef, "data-proctor-cam": 'pip', autoPlay: true, muted: true, playsInline: true, className: 'h-full w-full scale-x-[-1] object-cover' })) : (_jsx(UserFallbackRenderer, { name: 'You' })), _jsxs("span", { className: 'absolute left-2 top-2 inline-flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white backdrop-blur', children: [_jsx("span", { className: 'h-1.5 w-1.5 animate-pulse rounded-full bg-red-500' }), " REC"] }), _jsx("span", { className: 'absolute bottom-2 left-2 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white backdrop-blur', children: "You" }), _jsx("div", { className: 'absolute bottom-2 right-2 flex items-center gap-1.5', children: _jsx("button", { type: 'button', onClick: toggleMic, title: micEnabled ? 'Mute microphone' : 'Unmute microphone', "aria-label": micEnabled ? 'Mute microphone' : 'Unmute microphone', "aria-pressed": !micEnabled, className: `grid h-7 w-7 place-items-center rounded-full text-white backdrop-blur transition ${micEnabled ? 'bg-black/55 hover:bg-black/70' : 'bg-red-500/80 hover:bg-red-500'}`, children: micEnabled ? (_jsx(Mic, { className: 'h-3.5 w-3.5' })) : (_jsx(MicOff, { className: 'h-3.5 w-3.5' })) }) })] }), _jsxs("div", { className: 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-soft)]', children: [_jsxs("div", { className: 'flex items-center justify-between border-b border-border px-4 py-3', children: [_jsxs("span", { className: 'flex items-center gap-2 text-sm font-semibold text-foreground', children: [_jsx("span", { className: 'grid h-6 w-6 place-items-center rounded-full text-primary-foreground', style: { backgroundImage: 'var(--gradient-primary)' }, children: _jsx(Sparkles, { className: 'h-3.5 w-3.5' }) }), "Live Transcript"] }), sttDeaf ? (_jsxs("span", { className: 'flex items-center gap-1.5 text-xs text-amber-600', children: [_jsx("span", { className: 'h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500' }), "Can\u2019t hear you \u2014 reconnecting"] })) : sttProcessing ? (_jsxs("span", { className: 'flex items-center gap-1.5 text-xs text-sky-600', children: [_jsx("span", { className: 'h-1.5 w-1.5 animate-pulse rounded-full bg-sky-500' }), "Processing your answer"] })) : (_jsxs("span", { className: 'flex items-center gap-1.5 text-xs text-emerald-600', children: [_jsx("span", { className: 'h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500' }), ' ', "Listening"] }))] }), _jsx("div", { ref: transcriptRef, className: 'flex-1 space-y-3 overflow-y-auto p-4', children: transcript.length === 0 ? (_jsx("p", { className: 'text-xs text-muted-foreground', children: "The conversation transcript will appear here as you talk with the interviewer." })) : (transcript.flatMap((m, i) => {
                                            const isYou = m.from !== 'bot';
                                            // TS-15: a submitted coding answer is source code, not prose.
                                            // This panel is on screen for the whole post-submit
                                            // walkthrough, so it was the FIRST place the candidate saw
                                            // their own code dumped as an unformatted wall of text with
                                            // the "Submitted code (python):" prefix still attached.
                                            // Rendered as one block — never split into chat bubbles.
                                            const submitted = parseSubmittedCode(m.text, m.codeSnippet);
                                            if (submitted) {
                                                return [
                                                    _jsxs("div", { children: [_jsx("p", { className: 'mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground', children: isYou ? 'You' : 'AI Interviewer' }), _jsx(CodeSnippet, { code: submitted.code, label: submitted.label, language: submitted.language, maxHeightClassName: 'max-h-48' })] }, `t-${i}-code`),
                                                ];
                                            }
                                            const parts = (m === null || m === void 0 ? void 0 : m.isFinal) ? splitIntoBubbles(m.text) : [m.text];
                                            return parts.map((part, pIdx) => (_jsxs("div", { children: [pIdx === 0 && (_jsx("p", { className: 'mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground', children: isYou ? 'You' : 'AI Interviewer' })), _jsx("div", { className: `break-words rounded-2xl px-3 py-2 text-[13px] leading-relaxed text-foreground ${isYou ? 'rounded-tr-sm bg-primary/10' : 'rounded-tl-sm bg-muted'}`, children: part })] }, `t-${i}-${pIdx}`)));
                                        })) })] })] }))] }), showSkipConfirm && (_jsx("div", { className: 'absolute inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm', children: _jsxs("div", { className: 'w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center shadow-[var(--shadow-card)]', children: [_jsx("h3", { className: 'text-lg font-bold text-foreground', children: "Skip this question?" }), _jsx("p", { className: 'mt-2 text-sm leading-relaxed text-muted-foreground', children: "You\u2019ll move on to the rest of the interview. Your current code is saved \u2014 you can reopen this question from the chat while time remains." }), _jsxs("div", { className: 'mt-6 flex flex-col gap-2.5', children: [_jsx("button", { onClick: () => setShowSkipConfirm(false), className: 'inline-flex h-11 items-center justify-center rounded-full text-sm font-semibold text-primary-foreground shadow-sm transition hover:opacity-95 active:scale-95', style: { backgroundImage: 'var(--gradient-primary)' }, children: "Keep coding" }), _jsx("button", { onClick: commitSkip, className: 'inline-flex h-11 items-center justify-center rounded-full border border-border text-sm font-semibold text-destructive transition hover:bg-destructive/10 active:scale-95', children: "Skip question" })] })] }) }))] }));
};
export default CodingEditor;
